// Mirrors api/src/models/lambda-tracker.ts (Admin > Lambda Tracker).

export type LambdaRunHealth = "green" | "yellow" | "red"
export type LambdaRunStatus = "OK" | "Warning" | "Error" | "Timeout" | "Crashed" | "NoLog" | "Missed"

export interface LambdaRun {
    key: string
    status: LambdaRunStatus
    health: LambdaRunHealth
    reason: string | null
    startAt: string | null
    finishedAt: string | null
    expectedAt: string | null
    lateByMinutes: number | null
    durationMs: number | null
    billedDurationMs: number | null
    memorySizeMb: number | null
    maxMemoryUsedMb: number | null
    initDurationMs: number | null
    costUsd: number | null
    estimated: boolean
    errorMessage: string | null
    awsRequestId: string | null
    source: "syncLog" | "cloudWatch" | "schedule"
}

export interface LambdaStats {
    totalRuns: number
    okCount: number
    warningCount: number
    errorCount: number
    missedCount: number
    errors7d: number
    successRate: number | null
    bestMs: number | null
    worstMs: number | null
    avgMs: number | null
    p95Ms: number | null
    avgBilledMs: number | null
    avgMemoryUsedMb: number | null
    peakMemoryUsedMb: number | null
    memorySizeMb: number | null
    ratePerMsUsd: number | null
    totalCostUsd: number | null
    avgCostPerRunUsd: number | null
    projectedMonthlyCostUsd: number | null
    runsPerMonthBySchedule: number | null
    monthlyCostByScheduleUsd: number | null
    lastSuccessAt: string | null
    bestRunKey: string | null
    worstRunKey: string | null
}

export interface LambdaDependentScreen {
    code: string
    name: string
    url: string
}

export type LambdaRunKind = "incremental" | "budgetYears" | "fiscalYear" | "fiscalYears" | "full" | "trigger"

export interface LambdaScheduleView {
    name: string
    expression: string
    timezone: string | null
    enabled: boolean
    input: string | null
}

export interface LambdaTrackerItem {
    id: string
    code: string
    displayName: string
    description: string | null
    externalSystem: string
    lambdaFunctionName: string | null
    /** Deployed function this environment uses (e.g. project_acumatica-production); null when Lambdas are off. */
    functionName: string | null
    /** Fallback 5-field UTC cron, used when EventBridge cannot be read. */
    scheduleCron: string | null
    schedules: LambdaScheduleView[]
    scheduleSource: "eventbridge" | "database" | "none"
    runKind: LambdaRunKind
    runnable: boolean
    manualRunActive: boolean
    nextExpectedAt: string | null
    graceMinutes: number
    isActive: boolean
    lastSync: string | null
    syncButtonScreens: string | null
    rdsTables: string | null
    acumaticaGIs: string | null
    endpointUrl: string | null
    notes: string | null
    dependentScreens: LambdaDependentScreen[]
    currentHealth: LambdaRunHealth | null
    /** Most recent runs only, oldest first; stats cover the whole window. */
    runs: LambdaRun[]
    runsInWindow: number
    stats: LambdaStats
}

export interface LambdaTrackerCloudWatch {
    /** CloudWatch figures cover runs from this date; older runs use SyncLog (cost estimated). */
    from?: string
    available: boolean
    errors: string[]
}

export interface LambdaTrackerEnvironment {
    suffix: string
    lambdasEnabled: boolean
    schedules: { available: boolean, error?: string }
}

export interface LambdaTrackerResult {
    from: string
    to: string
    environment: LambdaTrackerEnvironment
    cloudWatch: LambdaTrackerCloudWatch
    items: LambdaTrackerItem[]
}

export interface LambdaTrackerDetail {
    from: string
    to: string
    environment: LambdaTrackerEnvironment
    cloudWatch: LambdaTrackerCloudWatch
    item: LambdaTrackerItem
}

export interface ScreenDependencyLambda {
    id: string
    /** "code": generated from the code (/update-lambda-dependencies); "manual": added on the screen. */
    source: "code" | "manual"
    code: string
    displayName: string
    isActive: boolean
    currentHealth: LambdaRunHealth | null
    avgMs: number | null
    lastSync: string | null
    sortOrder: number
}

export interface ScreenDependency {
    screenCode: string
    screenName: string
    url: string
    health: LambdaRunHealth | null
    totalAvgMs: number | null
    slowestAvgMs: number | null
    lambdas: ScreenDependencyLambda[]
}

export interface LambdaScreenDependenciesResult {
    cloudWatch: LambdaTrackerCloudWatch
    screens: ScreenDependency[]
}

/** Screens and Lambdas the dependency editor can pick from. */
export interface LambdaDependencyOptions {
    screens: { code: string, name: string, url: string }[]
    lambdas: { id: string, code: string, displayName: string, externalSystem: string, isActive: boolean }[]
}

export type LambdaRunBatchStatus = "Queued" | "Running" | "Succeeded" | "Failed" | "Cancelled"
export type LambdaRunRequestStatus = "Queued" | "Running" | "Succeeded" | "Warning" | "Failed" | "Cancelled"

export interface LambdaRunRequestView {
    id: string
    scriptId: string
    code: string
    displayName: string
    functionName: string
    phase: number
    label: string
    status: LambdaRunRequestStatus
    errorMessage: string | null
    awsRequestId: string | null
    startedAt: string | null
    finishedAt: string | null
}

export interface LambdaRunBatchView {
    id: string
    kind: "single" | "all"
    status: LambdaRunBatchStatus
    syncFrom: string | null
    fiscalYears: number[]
    requestedByEmail: string
    createdAt: string
    startedAt: string | null
    finishedAt: string | null
    counts: Record<LambdaRunRequestStatus, number>
    requests: LambdaRunRequestView[]
}

export interface LambdaRunsResult {
    lambdasEnabled: boolean
    suffix: string
    batches: LambdaRunBatchView[]
}

export interface LambdaRunOptions {
    /** YYYY-MM-DD: sync changes since this date (backfill). Empty = the usual last 3 days. */
    syncFrom?: string
    fiscalYears?: number[]
}

export interface LambdaTrackerFields {
    displayName: string
    description: string
    lambdaFunctionName: string
    scheduleCron: string
    graceMinutes: number | string
    isActive: boolean
    syncButtonScreens: string
    rdsTables: string
    acumaticaGIs: string
    endpointUrl: string
    notes: string
}

const STATUS_LABEL: Record<LambdaRunStatus, string> = {
    OK: "Succeeded",
    Warning: "Warning",
    Error: "Failed",
    Timeout: "Timed out",
    Crashed: "Crashed",
    NoLog: "No result recorded",
    Missed: "Missed",
}

const HEALTH_LABEL: Record<LambdaRunHealth, string> = {
    green: "Healthy",
    yellow: "Needs attention",
    red: "Failing",
}

// Status is never colour alone: each health has its own icon shape too.
const HEALTH_ICON: Record<LambdaRunHealth, string> = {
    green: "mdi mdi-check-circle",
    yellow: "mdi mdi-alert",
    red: "mdi mdi-close-circle",
}

export const LambdaTrackerUI = {
    statusLabel: (s: LambdaRunStatus) => STATUS_LABEL[s],
    healthLabel: (h: LambdaRunHealth | null) => (h ? HEALTH_LABEL[h] : "No runs"),
    icon: (run: Pick<LambdaRun, "health" | "status">) =>
        run.status === "Missed" ? "mdi mdi-clock-alert" : HEALTH_ICON[run.health],
    healthIcon: (h: LambdaRunHealth | null) => (h ? HEALTH_ICON[h] : "mdi mdi-minus-circle-outline"),
    chipVariant: (h: LambdaRunHealth | null) =>
        h === "green" ? "success" as const : h === "yellow" ? "warning" as const : h === "red" ? "danger" as const : "light" as const,

    /** 850 ms · 12.3 s · 4m 05s · 1h 02m */
    duration: (ms: number | null | undefined): string => {
        if (ms === null || ms === undefined) return "-"
        if (ms < 1000) return `${Math.round(ms)} ms`
        const s = ms / 1000
        if (s < 60) return `${s.toFixed(1)} s`
        const m = Math.floor(s / 60)
        if (m < 60) return `${m}m ${String(Math.round(s % 60)).padStart(2, "0")}s`
        return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`
    },

    /** Lambda costs are fractions of a cent: show enough decimals to be useful. */
    cost: (usd: number | null | undefined): string => {
        if (usd === null || usd === undefined) return "-"
        if (usd === 0) return "$0.00"
        if (usd < 0.01) return `$${usd.toFixed(6)}`
        return `$${usd.toFixed(2)}`
    },

    /** Very small dollar amounts, e.g. the price of one millisecond. */
    rate: (usd: number | null | undefined): string => (usd === null || usd === undefined ? "-" : `$${usd.toExponential(2)}`),

    // Every-N-minutes crons read as "Every N min"; anything else is shown as the cron itself.
    schedule: (cron: string | null | undefined): string => {
        if (!cron) return "Not scheduled"
        const step = cron.trim().match(/^\*\/(\d+) \* \* \* \*$/)
        return step ? `Every ${step[1]} min` : `${cron} (UTC)`
    },

    /** Readable schedule list: "Every 3 min", "cron(40 3 ? * MON-FRI *) America/Vancouver"... */
    schedules: (item: Pick<LambdaTrackerItem, "schedules" | "scheduleCron">): string => {
        const enabled = item.schedules.filter(s => s.enabled)
        if (enabled.length === 0) return item.schedules.length ? "Disabled" : "Not scheduled"
        return enabled.map(s => {
            const step = s.expression.match(/^cron\((?:0|\*)\/(\d+) \* \? \* \* \*\)$/) ?? s.expression.match(/^(?:0|\*)\/(\d+) \* \* \* \*$/)
            if (step) return `Every ${step[1]} min`
            const rate = s.expression.match(/^rate\((\d+ \w+)\)$/)
            if (rate) return `Every ${rate[1]}`
            return `${s.expression}${s.timezone && s.timezone !== "UTC" ? ` ${s.timezone}` : " UTC"}`
        }).join(" · ")
    },

    runStatusVariant: (status: LambdaRunRequestStatus | LambdaRunBatchStatus) =>
        status === "Succeeded" ? "success" as const
            : status === "Warning" ? "warning" as const
                : status === "Failed" ? "danger" as const
                    : status === "Running" ? "info" as const
                        : status === "Queued" ? "primary" as const : "secondary" as const,

    runStatusIcon: (status: LambdaRunRequestStatus | LambdaRunBatchStatus) =>
        status === "Succeeded" ? "mdi mdi-check-circle"
            : status === "Warning" ? "mdi mdi-alert"
                : status === "Failed" ? "mdi mdi-close-circle"
                    : status === "Running" ? "mdi mdi-loading mdi-spin"
                        : status === "Queued" ? "mdi mdi-clock-outline" : "mdi mdi-cancel",

    /** What a manual run of this kind does, for the Run dialog. */
    runKindHelp: (kind: LambdaRunKind): string => ({
        incremental: "Syncs records changed since the date you choose (one run per month). Leave the date empty to sync the last 3 days.",
        budgetYears: "Syncs the budget lines of the fiscal years you choose.",
        fiscalYear: "Rebuilds the report for each fiscal year you choose (one run per year).",
        fiscalYears: "Rebuilds the report for the fiscal years you choose.",
        full: "Re-reads everything from the source. No date needed.",
        trigger: "Sends emails; it only runs on its schedule.",
    } as Record<LambdaRunKind, string>)[kind],

    memory: (mb: number | null | undefined): string => (mb === null || mb === undefined ? "-" : `${Math.round(mb)} MB`),

    percent: (ratio: number | null | undefined): string =>
        ratio === null || ratio === undefined ? "-" : `${(ratio * 100).toFixed(ratio === 1 ? 0 : 1)}%`,

    /** When the run happened, or for a missed run when it should have. */
    runTime: (run: LambdaRun): string | null => run.startAt ?? run.expectedAt,
}
